import React, { useEffect, useState } from "react";

import {
  Box,
  Flex,
  Stack,
  Text,
  SegmentedControl,
  AlertMessage,
} from "flicket-ui";
import { FormikHelpers } from "formik";
import { useGoogleLogin } from "react-google-login";

import { Footer, Layout, Menu, Seo } from "~components";
import { googleClientId } from "~config";
import {
  useAccount,
  useFacebookLogin,
  useIsMobile,
  useOrganization,
} from "~hooks";
import { sdk } from "~lib";

import {
  AuthContainer,
  ContentContainer,
  CreateAccountLink,
  LoginForm,
  MobileLogo,
  SocialButtons,
} from "~components/auth";
import { LoginValues } from "~form/login";
import { RequestMagicLinkInput, SocialAuthType } from "~graphql/sdk";
import { getError, handlePromise, showToast } from "~lib/helpers";
import { useRouter } from "next/router";

const handleMagicLink = async (orgId: string, input: RequestMagicLinkInput) =>
  handlePromise(async () => sdk({ orgId }).requestMagicLink({ input }));

const Login = () => {
  const router = useRouter();
  const isMobile = useIsMobile();

  const { login, isLoggingIn, handleSocialSuccess } = useAccount();
  const { organization, hasFeature } = useOrganization();
  const [magicLinkState, setMagicLinkState] = useState<{
    sent: boolean;
    error?: string;
  }>({ sent: false });

  const formType = router.query.type === "password" ? "password" : "link";

  useEffect(() => {
    if (!router.query.type) {
      void router.replace("/login?type=link");
    }
  }, [router]);

  const showSocialMediaLogin = hasFeature("ShowSocialMediaLogin");

  const { isFacebookLoading, handleFacebookLogin } = useFacebookLogin({
    onSuccess: handleSocialSuccess,
  });

  const { signIn } = useGoogleLogin({
    onSuccess: (response: any) => {
      handleSocialSuccess(
        response?.getAuthResponse(true)?.access_token,
        SocialAuthType.Google
      );
    },
    clientId: googleClientId,
    onFailure: (response) => {
      if (
        !["popup_closed_by_user", "idpiframe_initialization_failed"].includes(
          response?.error
        )
      ) {
        showToast("Error logging in with Google", "error");
      }
    },
  });

  const handleSubmit = async (
    values: LoginValues[typeof formType],
    actions: FormikHelpers<LoginValues[typeof formType]>
  ) => {
    if (formType === "link") {
      const magicLinkInput: RequestMagicLinkInput = {
        ...values,
        redirectUrl: decodeURIComponent(
          (router.query.redirect as string) ?? ""
        ),
      };

      const { data, error } = await handleMagicLink(
        organization?.id,
        magicLinkInput
      );

      if (error) {
        setMagicLinkState({ sent: false, error: getError(error, "graphQL") });
      }

      if (data) {
        setMagicLinkState({ sent: true });
      }

      actions.setSubmitting(false);
      return;
    }

    const success = await login(values as LoginValues["password"]);

    if (!success) {
      actions.setSubmitting(false);
    }
  };

  const onSubmit = (
    values: LoginValues[typeof formType],
    actions: FormikHelpers<LoginValues[typeof formType]>
  ) => {
    handleSubmit(values, actions).catch(console.error);
  };

  return (
    <>
      <Seo
        title="Login"
        description={`Sign in to your ${organization?.name} account.`}
      />
      <Menu />
      <Layout layoutType="auth">
        <ContentContainer>
          <MobileLogo logo={organization?.branding?.logo} />
          <Flex
            bg="N100"
            justifyContent="flex-start"
            flex={1}
            alignItems="center"
            flexDir="column"
          >
            <AuthContainer>
              <Box w="100%">
                <Text variant="header.M" textAlign="center" mb={3}>
                  Log in
                </Text>
                <Stack flexDir="column" alignItems="flex-start" gap={5} mb={3}>
                  <SegmentedControl
                    onChange={(value) => {
                      void router.push(`/login?type=${value}`);
                    }}
                    data={[
                      { value: "link", label: "Send login link" },
                      { value: "password", label: "Password login" },
                    ]}
                    value={formType}
                    width="100%"
                  />
                </Stack>
                <Stack my={2} width={"100%"}>
                  {magicLinkState.sent && (
                    <AlertMessage
                      description={
                        <>
                          If an account exists with this email address,
                          you&apos;ll receive a login link shortly. Please check
                          your inbox and spam folder.
                        </>
                      }
                      title="Login link sent"
                      type="success"
                      size="md"
                      onDismiss={() => setMagicLinkState({ sent: false, error: undefined })}
                      width={"100%"}
                    />
                  )}
                  {magicLinkState.error && (
                    <AlertMessage
                      // Generic message to avoid leaking info about account existence.
                      // This method should always just return true anyway.
                      description={<>Please try again</>}
                      title="Error sending login link"
                      type="error"
                      size="md"
                      onDismiss={() => setMagicLinkState({ sent: false, error: undefined })}
                      width={"100%"}
                    />
                  )}
                </Stack>
                <LoginForm formType={formType} handleSubmit={onSubmit} />
                {showSocialMediaLogin && (
                  <SocialButtons
                    handleGoogleClick={signIn}
                    isFacebookLoading={isFacebookLoading || isLoggingIn}
                    handleFacebookClick={handleFacebookLogin}
                    isGoogleLoading={isLoggingIn}
                  />
                )}
              </Box>
            </AuthContainer>
            <Flex
              justifyContent="center"
              alignItems="center"
              mt={2}
              mb={{ _: 3, md: "28px" as any }}
            >
              <CreateAccountLink />
            </Flex>
          </Flex>
          {!isMobile && <Footer />}
        </ContentContainer>
      </Layout>
    </>
  );
};

export default Login;
